import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIEN = ['Chemie', 'Koffie', 'Schoonmaak', 'Techniek', 'Energie', 'Water', 'Afval', 'Anders'];
const OPZEGTERMIJNEN = ['1 maand', '2 maanden', '3 maanden', '6 maanden', '12 maanden'];
const BETALINGSTERMIJNEN = ['14 dagen', '30 dagen', '60 dagen'];

const LeveranciersTab = ({ locatieId, locatieData, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingLeverancier, setEditingLeverancier] = useState(null);

  const getContractStatus = (einddatum) => {
    if (!einddatum) return { color: 'text-slate-400', bg: 'bg-slate-100', label: 'Geen contract' };

    const today = new Date();
    const endDate = new Date(einddatum);
    const daysLeft = Math.floor((endDate - today) / (1000 * 60 * 60 * 24));
    const monthsLeft = daysLeft / 30;

    if (daysLeft < 0) return { color: 'text-red-600', bg: 'bg-red-100', label: 'Verlopen' };
    if (daysLeft < 30) return { color: 'text-red-600', bg: 'bg-red-100', label: '< 1 maand' };
    if (monthsLeft < 6) return { color: 'text-amber-600', bg: 'bg-amber-100', label: '< 6 maanden' };
    return { color: 'text-emerald-600', bg: 'bg-emerald-100', label: '> 6 maanden' };
  };

  const handleEdit = (leverancier) => {
    setEditingLeverancier(leverancier);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingLeverancier(null);
    setShowModal(true);
  };

  const handleDelete = async (leverancierId) => {
    if (!window.confirm('Weet je zeker dat je deze leverancier wilt verwijderen?')) return;

    try {
      await axios.delete(`${API}/locaties/${locatieId}/leveranciers/${leverancierId}`);
      toast.success('Leverancier verwijderd');
      onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  const leveranciers = locatieData?.leveranciers || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Leveranciers</h3>
          <p className="text-sm text-slate-500">{leveranciers.length} leveranciers</p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Leverancier toevoegen
        </Button>
      </div>

      {leveranciers.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <p className="text-slate-500">Geen leveranciers toegevoegd</p>
          <Button className="mt-4" onClick={handleAdd}>
            Eerste leverancier toevoegen
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bedrijfsnaam</TableHead>
                <TableHead>Categorie</TableHead>
                <TableHead>Contactpersoon</TableHead>
                <TableHead>Telefoon</TableHead>
                <TableHead>Contract Status</TableHead>
                <TableHead>Kosten/jaar</TableHead>
                <TableHead className="text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leveranciers.map((lev) => {
                const contract = lev.contract || {};
                const status = getContractStatus(contract.einddatum);
                return (
                  <TableRow key={lev._id}>
                    <TableCell className="font-medium">{lev.bedrijfsnaam}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                        {lev.categorie}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-600">{lev.contactpersoon || '-'}</TableCell>
                    <TableCell className="text-slate-600">{lev.telefoon || '-'}</TableCell>
                    <TableCell>
                      {contract.heeftContract ? (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${status.bg} ${status.color}`}>
                          {status.label}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-sm">Geen contract</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {contract.kostenPerJaar ? `€ ${contract.kostenPerJaar.toLocaleString()}` : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(lev)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(lev._id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <LeverancierModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingLeverancier(null);
        }}
        leverancier={editingLeverancier}
        locatieId={locatieId}
        onUpdate={onUpdate}
      />
    </div>
  );
};

const LeverancierModal = ({ open, onClose, leverancier, locatieId, onUpdate }) => {
  const [formData, setFormData] = useState({
    categorie: '',
    bedrijfsnaam: '',
    contactpersoon: '',
    telefoon: '',
    email: '',
    notities: '',
    contract: {
      heeftContract: false,
      contractnummer: '',
      startdatum: '',
      einddatum: '',
      opzegtermijn: '',
      kostenPerMaand: '',
      kostenPerJaar: '',
      betalingstermijn: '',
      automatischVerlengen: false
    }
  });

  React.useEffect(() => {
    if (leverancier) {
      setFormData({
        categorie: leverancier.categorie || '',
        bedrijfsnaam: leverancier.bedrijfsnaam || '',
        contactpersoon: leverancier.contactpersoon || '',
        telefoon: leverancier.telefoon || '',
        email: leverancier.email || '',
        notities: leverancier.notities || '',
        contract: {
          heeftContract: leverancier.contract?.heeftContract || false,
          contractnummer: leverancier.contract?.contractnummer || '',
          startdatum: leverancier.contract?.startdatum || '',
          einddatum: leverancier.contract?.einddatum || '',
          opzegtermijn: leverancier.contract?.opzegtermijn || '',
          kostenPerMaand: leverancier.contract?.kostenPerMaand || '',
          kostenPerJaar: leverancier.contract?.kostenPerJaar || '',
          betalingstermijn: leverancier.contract?.betalingstermijn || '',
          automatischVerlengen: leverancier.contract?.automatischVerlengen || false
        }
      });
    } else {
      // Reset for new
      setFormData({
        categorie: '',
        bedrijfsnaam: '',
        contactpersoon: '',
        telefoon: '',
        email: '',
        notities: '',
        contract: {
          heeftContract: false,
          contractnummer: '',
          startdatum: '',
          einddatum: '',
          opzegtermijn: '',
          kostenPerMaand: '',
          kostenPerJaar: '',
          betalingstermijn: '',
          automatischVerlengen: false
        }
      });
    }
  }, [leverancier, open]);

  const handleSubmit = async () => {
    if (!formData.bedrijfsnaam || !formData.categorie) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    try {
      if (leverancier) {
        await axios.put(`${API}/locaties/${locatieId}/leveranciers/${leverancier._id}`, formData);
        toast.success('Leverancier bijgewerkt');
      } else {
        await axios.post(`${API}/locaties/${locatieId}/leveranciers`, formData);
        toast.success('Leverancier toegevoegd');
      }
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Fout bij opslaan');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleContractChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contract: { ...prev.contract, [field]: value }
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{leverancier ? 'Leverancier bewerken' : 'Leverancier toevoegen'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Bedrijfsnaam *</Label>
              <Input
                value={formData.bedrijfsnaam}
                onChange={(e) => handleChange('bedrijfsnaam', e.target.value)}
                placeholder="Kenotek"
              />
            </div>
            <div>
              <Label>Categorie *</Label>
              <Select value={formData.categorie} onValueChange={(v) => handleChange('categorie', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIEN.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Contactpersoon</Label>
              <Input
                value={formData.contactpersoon}
                onChange={(e) => handleChange('contactpersoon', e.target.value)}
                placeholder="Jan de Vries"
              />
            </div>
            <div>
              <Label>Telefoon</Label>
              <Input
                value={formData.telefoon}
                onChange={(e) => handleChange('telefoon', e.target.value)}
                placeholder="010-1234567"
              />
            </div>
          </div>

          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="info@leverancier.nl"
            />
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center space-x-2 mb-4">
              <Switch
                checked={formData.contract.heeftContract}
                onCheckedChange={(v) => handleContractChange('heeftContract', v)}
              />
              <Label>Contract aanwezig</Label>
            </div>

            {formData.contract.heeftContract && (
              <div className="space-y-4 pl-6">
                <div>
                  <Label>Contractnummer</Label>
                  <Input
                    value={formData.contract.contractnummer}
                    onChange={(e) => handleContractChange('contractnummer', e.target.value)}
                    placeholder="KEN-2024-001"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Startdatum</Label>
                    <Input
                      type="date"
                      value={formData.contract.startdatum}
                      onChange={(e) => handleContractChange('startdatum', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Einddatum</Label>
                    <Input
                      type="date"
                      value={formData.contract.einddatum}
                      onChange={(e) => handleContractChange('einddatum', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Kosten per maand (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.contract.kostenPerMaand}
                      onChange={(e) => handleContractChange('kostenPerMaand', parseFloat(e.target.value))}
                      placeholder="1200.50"
                    />
                  </div>
                  <div>
                    <Label>Kosten per jaar (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.contract.kostenPerJaar}
                      onChange={(e) => handleContractChange('kostenPerJaar', parseFloat(e.target.value))}
                      placeholder="14400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Opzegtermijn</Label>
                    <Select
                      value={formData.contract.opzegtermijn}
                      onValueChange={(v) => handleContractChange('opzegtermijn', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer" />
                      </SelectTrigger>
                      <SelectContent>
                        {OPZEGTERMIJNEN.map(term => (
                          <SelectItem key={term} value={term}>{term}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Betalingstermijn</Label>
                    <Select
                      value={formData.contract.betalingstermijn}
                      onValueChange={(v) => handleContractChange('betalingstermijn', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer" />
                      </SelectTrigger>
                      <SelectContent>
                        {BETALINGSTERMIJNEN.map(term => (
                          <SelectItem key={term} value={term}>{term}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.contract.automatischVerlengen}
                    onCheckedChange={(v) => handleContractChange('automatischVerlengen', v)}
                  />
                  <Label>Automatisch verlengen</Label>
                </div>
              </div>
            )}
          </div>

          <div>
            <Label>Notities</Label>
            <Textarea
              value={formData.notities}
              onChange={(e) => handleChange('notities', e.target.value)}
              placeholder="Extra opmerkingen..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSubmit}>
            {leverancier ? 'Bijwerken' : 'Toevoegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeveranciersTab;
